import React, {useEffect, useRef, useState} from "react";
import styles from "./combobox.module.scss";
import deleteTag from "../../../images/delete-tag-combobox.svg";
import { LandPlot } from "../../../interfaces/card";
import { FormationTypeNames } from "../../../services/reducers/master-plan-modification";
import CustomScrollbar from "../scrollbar/scrollbar";

interface ComboboxProps {
  data: LandPlot[];
  max?: number;
  disabled: boolean;
  placeholder: string;
  selected: Array<LandPlot>;
  setSelected: (item: Array<LandPlot>) => void;
  value: string;
  setValue: (value: string) => void;
  selectedFormationType: FormationTypeNames | null;
}

const Combobox = ({
  data,
  max,
  disabled,
  placeholder,
  selected,
  setSelected,
  value,
  setValue,
  selectedFormationType,
}: ComboboxProps) => {
  const [dropdownOpened, setDropdownOpened] = useState<boolean>(false);
  const dropdownContentRef = useRef<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);

  useEffect(() => {
    if (dropdownOpened && dropdownContentRef.current) {
      const height = dropdownContentRef.current.scrollHeight;
      setContentHeight(height);
    }
  }, [dropdownOpened, data]);

  const addSelected = (plot: LandPlot) => {
    if (max && selected.length === max) {
      setSelected([plot]);
      setValue("");
      setDropdownOpened(false);
      return;
    } else {
      const newSelected = [...selected, plot];
      setSelected(newSelected);
      setValue("");
      setDropdownOpened(false);
      return;
    }
  };

  const deleteSelected = (plot: LandPlot) => {
    const newValue = selected.filter((selectedPlot) => selectedPlot !== plot);
    setSelected(newValue);
    return;
  };

  const setSearchValidatedValue = (value: string) => {
    setValue(value.trim());
  };

  const [filteredData, setFilteredData] = useState<LandPlot[] | null>(null);

  useEffect(() => {
    setFilteredData(
      data.filter((item: LandPlot) =>
        `Plot number: ${item.number}`.includes(value),
      ),
    );
  }, [value])

  const highlightText = (text: string, query: string) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, index) =>
        regex.test(part) ? <span key={index} style={{ fontWeight: 700 }}>{part}</span> : part
    );
  };

  return (
    <div className={styles.comboboxWrapper}>
      <label
          className={`
          ${disabled ? styles.disabled : ""} 
          ${dropdownOpened ? styles.opened : ""} 
          ${(value || selected.length > 0) ? styles.filled : ""}
        `}
      >
        {selected?.map((item: LandPlot) => (
          <span key={item.fid}
            className={`${selectedFormationType === FormationTypeNames.Merge ? styles.merge : ""}`}
          >
            Plot number: {item.number}{" "}
            {selectedFormationType === FormationTypeNames.Merge && (
              <button >
                <img onClick={() => deleteSelected(item)} src={deleteTag} />
              </button>
            )}
          </span>
        ))}
        <input
          disabled={disabled}
          placeholder={selected.length === 0 ? placeholder : ""}
          value={value}
          onChange={(e) => setSearchValidatedValue(e.currentTarget.value)}
          onFocus={() => setDropdownOpened(true)}
          onBlur={() => setDropdownOpened(false)}
        />
        {value || selected.length > 0 ? (
          <button
              onClick={() => {
                setSelected([])
                setValue("")
              }}
              className={`${styles.inputButton} ${styles.cross}`}>
          </button>
        ) : (
          <button className={`${styles.inputButton} ${styles.search}`}></button>
        )}
      </label>
      {dropdownOpened && (
            <div ref={dropdownContentRef} className={styles.dropdown}>
              <CustomScrollbar minHeight={contentHeight && contentHeight < 150 ? `${contentHeight}px` : "150px"}>
              {data
                  .filter(
                      (item: LandPlot) =>{
                          if (item.number)
                return item.number.toLowerCase().includes(value !== null ? value.toLowerCase() : "") &&
                          !selected.includes(item)}
                  )
                  .map((item: LandPlot) => (
                      <div
                          key={item.objectId}
                          className={styles.dropdownItem}
                          onMouseDown={(event) => addSelected(item)}
                      >
                        {highlightText(`Plot number: ${item.number}`, value)}
                      </div>
                  ))}
              </CustomScrollbar>
            </div>
      )}
    </div>
  );
};

export default Combobox;
